import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  X, Bug, Lightbulb, HelpCircle, Upload, MessageSquarePlus,
  Grid, MessageSquare, ShieldCheck, ExternalLink, LayoutDashboard, KeyRound,
} from 'lucide-react';
import './GarageFeedbackModal.css';

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'BUG', icon: Bug },
  { value: 'feature', label: 'FEATURE', icon: Lightbulb },
  { value: 'other', label: 'OTHER', icon: HelpCircle },
];

const RELATED_TO_OPTIONS = [
  { value: 'discover', label: 'Discover', icon: Grid },
  { value: 'requests', label: 'Requests', icon: MessageSquare },
  { value: 'admin', label: 'Admin', icon: ShieldCheck },
  { value: 'launch', label: 'Launch / RC App', icon: ExternalLink },
  { value: 'roi', label: 'Dashboard / ROI', icon: LayoutDashboard },
  { value: 'access', label: 'Access', icon: KeyRound },
  { value: 'other', label: 'Other', icon: HelpCircle },
];

/**
 * Product feedback modal (platform Bug / Feature / Other) — not tool star-reviews.
 */
const GarageFeedbackModal = ({ isOpen, onClose, onSubmit, userEmail, userName }) => {
  const [feedbackType, setFeedbackType] = useState('bug');
  const [relatedTo, setRelatedTo] = useState(null);
  const [comments, setComments] = useState('');
  const [outputComments, setOutputComments] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setFeedbackType('bug');
      setRelatedTo(null);
      setComments('');
      setOutputComments('');
      setScreenshot(null);
      setScreenshotPreview(null);
      setError(null);
      setSubmitted(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Please upload an image file (JPEG, PNG, GIF, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Screenshot must be under 10 MB.');
      return;
    }
    setError(null);
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  }, []);

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const showRelatedTo = feedbackType === 'bug' || feedbackType === 'feature';
  const commentsLabel = feedbackType === 'bug' ? 'WHAT WENT WRONG?' : 'WHAT IS YOUR IDEA?';
  const commentsPlaceholder =
    feedbackType === 'bug' ? 'Describe the error...' : 'Describe the feature...';

  const handleSubmit = async () => {
    if (!comments.trim()) {
      setError(
        feedbackType === 'bug' ? 'Please describe what went wrong.' : 'Please describe your idea.'
      );
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit({
        feedbackType,
        relatedTo: showRelatedTo ? relatedTo : null,
        comments: comments.trim(),
        outputComments: feedbackType === 'bug' ? outputComments.trim() : null,
        screenshot,
        userEmail,
        userName,
      });
      setSubmitted(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="gfb-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="gfb-modal">
        <div className="gfb-header">
          <div className="gfb-header-title">
            <div className="gfb-header-icon">
              <MessageSquarePlus size={18} />
            </div>
            <span>Submit Feedback</span>
          </div>
          <button type="button" className="gfb-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="gfb-success">
            <div className="gfb-success-icon">✓</div>
            <p>Thank you for your feedback!</p>
          </div>
        ) : (
          <div className="gfb-body">
            <div className="gfb-section">
              <label className="gfb-label">FEEDBACK TYPE</label>
              <div className="gfb-type-tabs">
                {FEEDBACK_TYPES.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    className={`gfb-type-tab ${feedbackType === value ? 'active' : ''}`}
                    onClick={() => {
                      setFeedbackType(value);
                      setRelatedTo(null);
                      setComments('');
                      setOutputComments('');
                      setError(null);
                    }}
                  >
                    <Icon size={14} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {showRelatedTo && (
              <div className="gfb-section">
                <label className="gfb-label">RELATED TO</label>
                <div className="gfb-related-options">
                  {RELATED_TO_OPTIONS.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      className={`gfb-related-btn ${relatedTo === value ? 'active' : ''}`}
                      onClick={() => setRelatedTo(relatedTo === value ? null : value)}
                    >
                      <Icon size={16} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="gfb-section">
              <label className="gfb-label">
                {commentsLabel} <span className="gfb-required">*</span>
              </label>
              <textarea
                className="gfb-textarea"
                placeholder={commentsPlaceholder}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={4}
              />
            </div>

            {feedbackType === 'bug' && (
              <div className="gfb-section">
                <label className="gfb-label">WHAT DID YOU EXPECT?</label>
                <textarea
                  className="gfb-textarea"
                  placeholder="I expected..."
                  value={outputComments}
                  onChange={(e) => setOutputComments(e.target.value)}
                  rows={3}
                />
              </div>
            )}

            <div className="gfb-section">
              <label className="gfb-label">SCREENSHOT (OPTIONAL)</label>
              {screenshotPreview ? (
                <div className="gfb-screenshot-preview">
                  <img src={screenshotPreview} alt="Screenshot preview" />
                  <button type="button" className="gfb-remove-screenshot" onClick={removeScreenshot}>
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div
                  className={`gfb-upload-zone ${isDraggingOver ? 'drag-over' : ''}`}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingOver(false);
                    handleFileSelect(e.dataTransfer.files[0]);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingOver(true);
                  }}
                  onDragLeave={() => setIsDraggingOver(false)}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                >
                  <Upload size={22} className="gfb-upload-icon" />
                  <span>Click or drag to upload</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileSelect(e.target.files?.[0])}
                  />
                </div>
              )}
            </div>

            {error && <div className="gfb-error">{typeof error === 'string' ? error : 'Failed to submit feedback.'}</div>}

            <button
              type="button"
              className="gfb-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <span className="gfb-spinner" /> : 'Submit Feedback'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GarageFeedbackModal;
